/* eslint-disable complexity */
import {
  record_manual_verification_decision,
  record_manual_verification_field_result
} from '@/services/onboarding_journey_service';
import {
  build_field_run_by_question,
  build_manual_field_choice_payload,
  build_manual_mapping_questions,
  build_manual_mapping_section_payload,
  build_manual_review_sections_from_form_sections,
  build_manual_workspace_decision_payload,
  decorate_review_form_section,
  get_manual_workspace_decision_warning,
  get_terminal_manual_run_message,
  is_terminal_manual_run_error,
  manual_mappings_have_onboarding_links,
  manual_choice_status,
  manual_field_key,
  manual_run_is_terminal
} from './manage_verification_manual_helpers';

function merge_manual_field_runs(field_runs = [], target_question_id = 0, next_field_run = {}) {
  const source_field_runs = Array.isArray(field_runs) ? field_runs : [];
  const has_existing_run = source_field_runs.some((field_run) => (
    Number(field_run?.section_question || 0) === target_question_id
  ));

  if (!has_existing_run) {
    return [...source_field_runs, next_field_run];
  }

  return source_field_runs.map((field_run) => {
    return Number(field_run?.section_question || 0) === target_question_id
      ? { ...field_run, ...next_field_run }
      : field_run;
  });
}

function update_manual_check_item(check_item = {}, target_run_id = 0, target_question_id = 0, next_field_run = {}) {
  const check_run_id = Number(check_item?.run_id || check_item?.manual_run?.id || 0);
  if (check_run_id !== target_run_id) {
    return check_item;
  }

  const next_field_runs = merge_manual_field_runs(
    check_item?.field_runs || check_item?.manual_run?.field_runs,
    target_question_id,
    next_field_run
  );
  return {
    ...check_item,
    field_runs: next_field_runs,
    manual_run: check_item?.manual_run
      ? {
        ...check_item.manual_run,
        field_runs: next_field_runs
      }
      : check_item.manual_run
  };
}

function update_manual_modal_item(modal = {}, target_run_id = 0, target_question_id = 0, next_field_run = {}) {
  if (!modal?.item) return modal;

  const modal_run_id = Number(modal.item?.run_id || modal.item?.manual_run?.id || 0);
  if (modal_run_id !== target_run_id) {
    return modal;
  }

  const next_modal_field_runs = merge_manual_field_runs(
    modal.item?.field_runs || modal.item?.manual_run?.field_runs,
    target_question_id,
    next_field_run
  );
  return {
    ...modal,
    item: {
      ...modal.item,
      field_runs: next_modal_field_runs,
      manual_run: modal.item?.manual_run
        ? {
          ...modal.item.manual_run,
          field_runs: next_modal_field_runs
        }
        : modal.item?.manual_run
    }
  };
}

export const manageVerificationManualMethods = {
  build_local_manual_field_run(manual_item = {}, item = {}, match_status = '') {
    const target_run_id = Number(manual_item?.run_id || manual_item?.manual_run?.id || 0) || null;
    const target_question_id = Number(item?.section_question_id || item?.id || 0) || null;
    const existing_field_run = this.get_manual_field_runs(manual_item).find((field_run) => (
      Number(field_run?.section_question || 0) === target_question_id
    )) || null;

    const local_field_run = {
      ...(existing_field_run || {}),
      id: existing_field_run?.id || `${target_run_id || 'manual'}-${target_question_id || item?.id || 'field'}`,
      run: existing_field_run?.run || target_run_id,
      run_id: existing_field_run?.run_id || target_run_id,
      section_question: target_question_id,
      field_label_snapshot: existing_field_run?.field_label_snapshot || item?.label || 'Verification field',
      user_answer: item?.answer || existing_field_run?.user_answer || '',
      verified_value: match_status === 'match'
        ? (item?.answer || existing_field_run?.verified_value || '')
        : (existing_field_run?.verified_value || ''),
      match_status,
      reviewer_comment: item?.comment || existing_field_run?.reviewer_comment || '',
      source_evidence: item?.source_label || item?.detail || existing_field_run?.source_evidence || ''
    };
    return local_field_run;
  },
  sync_manual_field_run_locally(manual_item = {}, item = {}, match_status = '') {
    const target_run_id = Number(manual_item?.run_id || manual_item?.manual_run?.id || 0);
    const target_question_id = Number(item?.section_question_id || item?.id || 0);
    if (!target_run_id || !target_question_id) {
      return;
    }

    const next_field_run = this.build_local_manual_field_run(manual_item, item, match_status);
    this.checks = (Array.isArray(this.checks) ? this.checks : []).map((check_item) => (
      update_manual_check_item(check_item, target_run_id, target_question_id, next_field_run)
    ));
    this.manual_review_modal = update_manual_modal_item(
      this.manual_review_modal,
      target_run_id,
      target_question_id,
      next_field_run
    );
  },
  update_manual_checklist_status(item_id, status) {
    const checklist_items = this.manual_workspace_checklist_items();
    this.manual_workspace = {
      ...this.manual_workspace,
      checklist_items: checklist_items.map(function update_item(item) {
        return item.id === item_id ? { ...item, status } : item;
      })
    };
  },
  update_manual_checklist_comment(item_id, comment) {
    const checklist_items = this.manual_workspace_checklist_items();
    this.manual_workspace = {
      ...this.manual_workspace,
      checklist_items: checklist_items.map(function update_item(item) {
        return item.id === item_id ? { ...item, comment } : item;
      })
    };
  },
  manual_checklist_status_class(status = '') {
    if (status === 'pass') return 'text-success';
    if (status === 'fail') return 'text-danger';
    if (status === 'na') return 'text-muted';
    return 'text-muted';
  },
  async save_manual_review_draft() {
    this.set_run_feedback('Manual review draft saved locally for this session.', 'success');
  },
  async submit_manual_workspace_decision(action = '') {
    if (this.is_entity_verification_read_only) {
      this.set_run_feedback('Verification is view-only for entity users.', 'warning');
      return;
    }

    const manual_item = this.manual_workspace_item;
    if (manual_run_is_terminal(manual_item)) {
      this.set_run_feedback(get_terminal_manual_run_message(manual_item), 'warning');
      return;
    }

    const reviewer_comments = String(this.manual_workspace.reviewer_comments || '').trim();
    const warning_message = get_manual_workspace_decision_warning(manual_item, reviewer_comments);
    if (warning_message) {
      this.set_run_feedback(warning_message, 'warning');
      return;
    }

    this.manual_review_modal = { ...this.manual_review_modal, is_submitting: true };

    try {
      const payload = build_manual_workspace_decision_payload(manual_item, action, reviewer_comments);
      await record_manual_verification_decision(payload);
      this.manual_workspace = { ...this.manual_workspace, decision_status: action };
      const decision_label = typeof this.manual_decision_label === 'function'
        ? this.manual_decision_label()
        : 'decision';
      this.set_run_feedback(`Manual verification decision recorded: ${decision_label}.`, 'success');
      await this.load_checks();
    } catch (error) {
      this.set_run_feedback(error?.response?.data?.message || error?.message || 'Unable to submit the manual verification decision.', 'danger');
    } finally {
      this.manual_review_modal = { ...this.manual_review_modal, is_submitting: false };
    }
  },
  manual_field_choice_is_saving(item = {}) {
    return this.running_manual_field_keys.includes(manual_field_key(item));
  },
  manual_workspace_is_terminal() {
    return manual_run_is_terminal(this.manual_workspace_item || {});
  },
  manual_field_choice_disabled(item = {}) {
    return this.is_entity_verification_read_only
      || this.manual_workspace_is_terminal()
      || this.manual_field_choice_is_saving(item);
  },
  manual_field_choice_disabled_reason() {
    if (this.is_entity_verification_read_only) {
      return 'Verification is view-only for entity users.';
    }

    return this.manual_workspace_is_terminal()
      ? get_terminal_manual_run_message(this.manual_workspace_item || {})
      : '';
  },
  set_manual_field_choice_saving(item = {}, is_saving = false) {
    const key = manual_field_key(item);
    if (is_saving) {
      if (!this.running_manual_field_keys.includes(key)) {
        this.running_manual_field_keys = [...this.running_manual_field_keys, key];
      }
      return;
    }

    this.running_manual_field_keys = this.running_manual_field_keys.filter((value) => value !== key);
  },
  async record_manual_checklist_choice(item = {}, match_status = '', event = null) {
    event?.stopPropagation?.();
    if (this.is_entity_verification_read_only) {
      this.set_run_feedback('Verification is view-only for entity users.', 'warning');
      return;
    }

    const manual_item = this.manual_workspace_item;
    if (!manual_item?.run_id || !item?.section_question_id) {
      this.set_run_feedback('Open the manual review before recording field outcomes.', 'warning');
      return;
    }
    if (manual_run_is_terminal(manual_item)) {
      this.set_run_feedback(get_terminal_manual_run_message(manual_item), 'warning');
      return;
    }

    this.set_manual_field_choice_saving(item, true);
    try {
      await record_manual_verification_field_result(
        build_manual_field_choice_payload(manual_item, item, match_status)
      );
      this.update_manual_checklist_status(item.id, manual_choice_status(match_status));
      this.sync_manual_field_run_locally(manual_item, item, match_status);
      this.set_run_feedback('Manual field outcome recorded.', 'success');
    } catch (error) {
      if (is_terminal_manual_run_error(error)) {
        this.set_run_feedback(get_terminal_manual_run_message(manual_item), 'warning');
        return;
      }
      this.set_run_feedback(error?.response?.data?.message || error?.message || 'Unable to record the field outcome.', 'danger');
    } finally {
      this.set_manual_field_choice_saving(item, false);
    }
  },
  get_manual_field_runs(item = {}) {
    const field_runs = item?.field_runs || item?.manual_run?.field_runs;
    return Array.isArray(field_runs) ? field_runs : [];
  },
  get_manual_review_form_sections() {
    return Array.isArray(this.manual_review_modal?.form_sections)
      ? this.manual_review_modal.form_sections
      : [];
  },
  manual_applicant_form_id(form_section = {}) {
    return `manual-applicant-form-${form_section?.id || form_section?.form_name || 'form'}`;
  },
  sync_expanded_manual_applicant_forms() {
    this.expanded_manual_applicant_form_ids = this.get_manual_applicant_form_sections()
      .map((form_section) => this.manual_applicant_form_id(form_section));
    this.manual_applicant_forms_expansion_initialized = true;
  },
  is_manual_applicant_form_expanded(form_section = {}) {
    const form_id = this.manual_applicant_form_id(form_section);
    if (!this.manual_applicant_forms_expansion_initialized) {
      this.sync_expanded_manual_applicant_forms();
    }
    return this.expanded_manual_applicant_form_ids.includes(form_id);
  },
  toggle_manual_applicant_form(form_section = {}) {
    const form_id = this.manual_applicant_form_id(form_section);
    if (this.expanded_manual_applicant_form_ids.includes(form_id)) {
      this.expanded_manual_applicant_form_ids = this.expanded_manual_applicant_form_ids
        .filter((item) => item !== form_id);
      return;
    }
    this.expanded_manual_applicant_form_ids = [...this.expanded_manual_applicant_form_ids, form_id];
  },
  build_manual_review_sections_from_mappings(item = {}) {
    const mappings = Array.isArray(item?.manual_mappings) ? item.manual_mappings : [];
    if (!mappings.length) return [];

    const resolve_answer = (mapping) => this.get_submitted_answer_for_mapping(mapping);
    return build_manual_mapping_section_payload(
      item,
      build_manual_mapping_questions(mappings, resolve_answer)
    );
  },
  manual_workspace_review_sections() {
    const manual_item = this.manual_workspace_item || {};
    const manual_form_sections = Array.isArray(manual_item?.manual_form_sections)
      ? manual_item.manual_form_sections
      : [];
    const manual_mappings = Array.isArray(manual_item?.manual_mappings)
      ? manual_item.manual_mappings
      : [];
    const source_sections = manual_mappings_have_onboarding_links(manual_mappings)
      ? this.build_manual_review_sections_from_mappings(manual_item)
      : manual_form_sections.length
      ? build_manual_review_sections_from_form_sections(
        manual_item,
        manual_form_sections,
        manual_mappings,
        (mapping) => this.get_submitted_answer_for_mapping(mapping)
      )
      : this.build_manual_review_sections_from_mappings(manual_item);
    const field_run_by_question = build_field_run_by_question(this.get_manual_field_runs(manual_item));

    return source_sections
      .map((form_section) => decorate_review_form_section(form_section, field_run_by_question))
      .filter((form_section) => form_section.sections.length);
  },
  open_manual_review_field_form(question = {}) {
    if (this.is_entity_verification_read_only) {
      return;
    }

    const existing_field_run = this.find_manual_field_run(question.id);
    this.manual_review_modal = {
      ...this.manual_review_modal,
      field_form: {
        section_question_id: Number(question?.id || 0) || null,
        field_label: question?.label || 'Reviewed field',
        user_answer: this.format_detail_value(question?.answer),
        verified_value: existing_field_run?.verified_value || '',
        match_status: existing_field_run?.match_status || 'unable_to_verify',
        reviewer_comment: existing_field_run?.reviewer_comment || '',
        source_evidence: existing_field_run?.source_evidence || ''
      }
    };
  },
  close_manual_review_field_form() {
    this.manual_review_modal = { ...this.manual_review_modal, field_form: null };
  },
  find_manual_field_run(section_question_id) {
    const target_id = Number(section_question_id || 0);
    if (!target_id) return null;

    return this.get_manual_field_runs(this.manual_review_modal?.item).find(
      function field_matches(field_run) {
        return Number(field_run?.section_question || 0) === target_id;
      }
    ) || null;
  },
  has_manual_field_run(section_question_id) {
    return Boolean(this.find_manual_field_run(section_question_id));
  },
  manual_field_run_badge_tone(section_question_id) {
    const field_run = this.find_manual_field_run(section_question_id);
    if (!field_run) return 'secondary';

    const normalized_match_status = this.normalize_match_status(field_run.match_status);
    if (normalized_match_status === 'match') return 'success';
    if (normalized_match_status === 'mismatch') return 'danger';
    return 'warning';
  },
  manual_field_run_badge_label(section_question_id) {
    const field_run = this.find_manual_field_run(section_question_id);
    return field_run ? this.format_match_status(field_run.match_status) : 'Not reviewed';
  },
  manual_review_summary_cards() {
    const progress = this.manual_review_progress;

    return [
      {
        label: 'Reviewed',
        value: `${progress.reviewed_questions}/${progress.total_questions || 0}`,
        detail: progress.remaining_questions ? `${progress.remaining_questions} fields remaining` : 'All visible fields reviewed',
        tone: progress.remaining_questions ? 'warning' : 'success'
      },
      {
        label: 'Matched',
        value: String(progress.matched_count),
        detail: 'Submitted answer matches evidence',
        tone: 'success'
      },
      {
        label: 'Mismatches',
        value: String(progress.mismatched_count),
        detail: 'Needs remediation or escalation',
        tone: 'danger'
      },
      {
        label: 'Unable to verify',
        value: String(progress.unable_to_verify_count + progress.partial_count),
        detail: 'Partial or missing evidence',
        tone: 'warning'
      }
    ];
  },
};
